import { useCallback, useEffect, useState } from 'react';

import { EVehicleType } from '@/redux/modules/globalHeader';
import { IVehicle } from '@/services/types/search/vehicle';

/**
 * Generic hook for managing initial input values in modal/filter components.
 * Captures initial state when opened and provides utilities for change detection.
 */
export function useInitialInputs<T>(
  currentValue: T,
  isOpened: boolean,
  compareFunction?: (current: T, initial: T) => boolean,
) {
  const [initialValue, setInitialValue] = useState<T>(currentValue);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (isOpened && !hasInitialized) {
      setInitialValue(currentValue);
      setHasInitialized(true);
      setHasChanges(false);
    } else if (!isOpened) {
      setHasInitialized(false);
    }
  }, [isOpened, currentValue, hasInitialized]);

  useEffect(() => {
    if (hasInitialized && compareFunction) {
      const hasCurrentChanges = compareFunction(currentValue, initialValue);

      if (hasCurrentChanges !== hasChanges) {
        setHasChanges(hasCurrentChanges);
      }
    }
  }, [currentValue, initialValue, hasInitialized, hasChanges, compareFunction]);

  const markAsChanged = useCallback(() => {
    setHasChanges(true);
  }, []);

  const setHasChangesValue = useCallback((value: boolean) => {
    setHasChanges(value);
  }, []);

  const resetToInitial = useCallback(() => {
    setHasChanges(false);
    return initialValue;
  }, [initialValue]);

  return {
    initialValue,
    hasChanges,
    hasInitialized,
    markAsChanged,
    setHasChangesValue,
    resetToInitial,
  };
}

export function useInitialAddress(addressValue: string, isOpened: boolean) {
  const addressSelection = addressValue;

  const compareAddress = useCallback(
    (current: typeof addressSelection, initial: typeof addressSelection) => {
      return current !== initial;
    },
    [],
  );

  const { initialValue, hasChanges, hasInitialized, markAsChanged, resetToInitial } =
    useInitialInputs(addressSelection, isOpened, compareAddress);

  const isInResetState = !addressValue;

  return {
    initialValue,
    hasChanges,
    hasInitialized,
    markAsChanged,
    resetToInitial,
    isInResetState,
  };
}

export function useInitialDates(
  datesValue: {
    from?: Date;
    to?: Date;
    flexible_days?: number;
    from_time?: number;
    to_time?: number;
  },
  isOpened: boolean,
) {
  const dateSelection = {
    from: datesValue?.from,
    to: datesValue?.to,
    flexible_days: datesValue?.flexible_days,
    from_time: datesValue?.from_time,
    to_time: datesValue?.to_time,
  };

  const compareDates = useCallback(
    (current: typeof dateSelection, initial: typeof dateSelection) => {
      return (
        current.from?.getTime() !== initial.from?.getTime() ||
        current.to?.getTime() !== initial.to?.getTime() ||
        current.flexible_days !== initial.flexible_days ||
        current.from_time !== initial.from_time ||
        current.to_time !== initial.to_time
      );
    },
    [],
  );

  const { initialValue, hasChanges, hasInitialized, markAsChanged, resetToInitial } =
    useInitialInputs(dateSelection, isOpened, compareDates);

  const isInResetState =
    !datesValue?.from &&
    !datesValue?.to &&
    !datesValue?.flexible_days &&
    datesValue?.from_time === undefined &&
    datesValue?.to_time === undefined;

  return {
    initialValue,
    hasChanges,
    hasInitialized,
    markAsChanged,
    resetToInitial,
    isInResetState,
  };
}

export function useInitialGuests(
  guestsValue: {
    adults?: number;
    children?: number;
    infants?: number;
    pet_friendly?: boolean;
    accessible?: boolean;
  },
  isOpened: boolean,
) {
  const guestSelection = {
    adults: guestsValue?.adults,
    children: guestsValue?.children,
    infants: guestsValue?.infants,
    pet_friendly: guestsValue?.pet_friendly,
    accessible: guestsValue?.accessible,
  };

  const compareGuests = useCallback(
    (current: typeof guestSelection, initial: typeof guestSelection) => {
      return (
        current.adults !== initial.adults ||
        current.children !== initial.children ||
        current.infants !== initial.infants ||
        current.pet_friendly !== initial.pet_friendly ||
        current.accessible !== initial.accessible
      );
    },
    [],
  );

  const { initialValue, hasChanges, hasInitialized, markAsChanged, resetToInitial } =
    useInitialInputs(guestSelection, isOpened, compareGuests);

  const isInResetState =
    (!guestsValue?.adults || guestsValue?.adults === 1) &&
    (!guestsValue?.children || guestsValue?.children === 0) &&
    (!guestsValue?.infants || guestsValue?.infants === 0) &&
    (guestsValue?.pet_friendly === undefined || guestsValue?.pet_friendly === false) &&
    (guestsValue?.accessible === undefined || guestsValue?.accessible === false);

  return {
    initialValue,
    hasChanges,
    hasInitialized,
    markAsChanged,
    resetToInitial,
    isInResetState,
  };
}

export function useInitialDriverAge(driverAgeValue: number | undefined, isOpened: boolean) {
  const driverAgeSelection = driverAgeValue;

  const compareDriverAge = useCallback(
    (current: typeof driverAgeSelection, initial: typeof driverAgeSelection) => {
      return current !== initial;
    },
    [],
  );

  const { initialValue, hasChanges, hasInitialized, markAsChanged, resetToInitial } =
    useInitialInputs(driverAgeSelection, isOpened, compareDriverAge);

  const isInResetState = driverAgeValue === undefined;

  return {
    initialValue,
    hasChanges,
    hasInitialized,
    markAsChanged,
    resetToInitial,
    isInResetState,
  };
}

export function useInitialVehicleType(
  vehicleTypeValue: {
    vehicleType: EVehicleType;
    vehicle?: IVehicle;
  },
  isOpened: boolean,
) {
  const vehicleTypeSelection = {
    vehicleType: vehicleTypeValue?.vehicleType,
    vehicle: vehicleTypeValue?.vehicle,
  };

  const compareVehicleType = useCallback(
    (current: typeof vehicleTypeSelection, initial: typeof vehicleTypeSelection) => {
      return (
        current.vehicleType !== initial.vehicleType ||
        current.vehicle?.id !== initial.vehicle?.id ||
        current.vehicle?.make !== initial.vehicle?.make ||
        current.vehicle?.model !== initial.vehicle?.model ||
        current.vehicle?.year !== initial.vehicle?.year
      );
    },
    [],
  );

  const { initialValue, hasChanges, hasInitialized, markAsChanged, resetToInitial } =
    useInitialInputs(vehicleTypeSelection, isOpened, compareVehicleType);

  const isInResetState = vehicleTypeValue?.vehicleType === EVehicleType.ALL;

  return {
    initialValue,
    hasChanges,
    hasInitialized,
    markAsChanged,
    resetToInitial,
    isInResetState,
  };
}

export function useInitialSearchMode(isDeliveryModeValue: boolean, isOpened: boolean) {
  const isDeliveryModeSelection = isDeliveryModeValue;

  const compareIsDeliveryMode = useCallback(
    (current: typeof isDeliveryModeSelection, initial: typeof isDeliveryModeSelection) => {
      return current !== initial;
    },
    [],
  );

  const { initialValue, hasChanges, hasInitialized, markAsChanged, resetToInitial } =
    useInitialInputs(isDeliveryModeSelection, isOpened, compareIsDeliveryMode);

  return {
    initialValue,
    hasChanges,
    hasInitialized,
    markAsChanged,
    resetToInitial,
  };
}
